import React, { useState, useEffect, useMemo, useCallback, type FC } from 'react';
import { Grid, Typography, CircularProgress, Link, Button } from '@mui/material';
import { isNil } from 'ramda';

import ContentLayout from '../../shared/ui/ContentLayout';
import { DashboardSection, LingoLiftDashboardSectionHeader, MainLayout } from '../../widgets';
import DropdownMenu from '../../shared/ui/DropdownMenu';
import {
  LingoLiftTeacherDashboardClassesList,
  LingoLiftTeacherDashboardFeedCard,
  LingoLiftTeacherDashboardTaskList,
  LingoLiftTeacherDashboardReport,
  useGetLingoLiftTeacherDashboardTasksQuery,
  useGetLingoLiftTeacherDashboardFeedQuery,
  useGetLingoLiftTeacherDashboardFilterQuery,
  useGetLingoLiftTeacherDashboardClassesQuery,
} from '../../entities/lingoLiftTeacherDashboard';
import Div from '../../shared/ui/Div';
import lingoLiftIcon from '../../assets/tabsLogos/lingoLiftIcon.svg';
import lingoLiftActiveIcon from '../../assets/tabsLogos/lingoLiftActiveIcon.svg';
import listenwiseIcon from '../../assets/tabsLogos/listenwiseIcon.svg';
import listenwiseActiveIcon from '../../assets/tabsLogos/listenwiseActiveIcon.svg';
import TabsContainer from '../../shared/ui/Tabs/ui';
import { TabContent } from '../../shared/ui/Tabs/ui/TabContent';
import PaginationLine from '../../shared/ui/PaginationLine';
import Loader from '../../shared/ui/Loader';
import { UserRole } from '../../entities/user';
import {
  type ILingoLiftTeacherDashboardClass,
  type ILingoLiftTeacherDashboardClassesLink,
} from '../../entities/lingoLiftTeacherDashboard/model/types';
import {
  type ILingoLiftFavoriteParams,
  useDeleteLingoLiftTeacherFavoriteMutation,
  usePostLingoLiftTeacherAddFavoriteMutation,
} from '../../entities/lingoLiftFavorite';
import { useSnackbar } from '../../shared/hooks/useSnackbar';
import { getFromLocalStorage, setToLocalStorage } from '../../utils/localStorageUtils';

import styles from './styles';

const LIMIT = 5;
const LIMIT_TASKS = 3;

interface ILingoLiftTeacherDashboardBrowsePageProps {
  isListenwise: boolean;
  isLingolift: boolean;
  teacherName: string;
  manageClassesUrl: string;
}
interface ReportTab {
  name: string;
  icon: string;
  activeIcon: string;
  isListenwise?: boolean;
  isLingolift?: boolean;
}

const LingoLiftTeacherDashboardBrowsePage: FC<
  ILingoLiftTeacherDashboardBrowsePageProps
> = props => {
  const { isListenwise, isLingolift, teacherName, manageClassesUrl } = props;
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedFilter, setSelectFilter] = useState<string>('All');
  const [schoolClassesDisplayedCount, setSchoolClassesDisplayedCount] = useState<number>(LIMIT);
  const [tasksDisplayedCount, setTasksDisplayedCount] = useState<number>(LIMIT_TASKS);
  const [sectionOrder, setSectionOrder] = useState<DashboardSection[]>(() => {
    const savedOrder = getFromLocalStorage('teacherDashboardSectionOrder');
    return savedOrder || ['tasks', 'classes', 'reports'];
  });
  const [viewReportsLink, setViewReportsLink] = useState<string | null>(null);
  const { showSuccessNotification, showErrorNotification } = useSnackbar();

  // Add state to track optimistic updates for each feed item
  const [optimisticFavorites, setOptimisticFavorites] = useState<Record<number, boolean>>({});

  const reportTabs: ReportTab[] = useMemo(() => {
    const tabs: ReportTab[] = [];
    if (isListenwise) {
      tabs.push({
        name: 'Listenwise',
        icon: listenwiseIcon,
        activeIcon: listenwiseActiveIcon,
        isListenwise: true,
        isLingolift: false,
      });
    }
    if (isLingolift) {
      tabs.push({
        name: 'Lingolift',
        icon: lingoLiftIcon,
        activeIcon: lingoLiftActiveIcon,
        isListenwise: false,
        isLingolift: true,
      });
    }
    return tabs;
  }, [isListenwise, isLingolift]);

  const { data: dashboardFilters = [], isLoading: isDashboardFiltersLoading } =
    useGetLingoLiftTeacherDashboardFilterQuery();

  const selectedFilterUrl = useMemo(
    () => dashboardFilters.find(filter => filter.label === selectedFilter),
    [dashboardFilters, selectedFilter],
  );

  const { data: dashboardFeed, isFetching } = useGetLingoLiftTeacherDashboardFeedQuery(
    {
      url: isNil(selectedFilterUrl) ? null : selectedFilterUrl?.url,
      params: { page: currentPage },
    },
    {
      skip: !dashboardFilters?.length,
    },
  );

  const [postLingoLiftTeacherDashboardToggleFavorite] =
    usePostLingoLiftTeacherAddFavoriteMutation();

  const [deleteLingoLiftTeacherDashboardFavorite] = useDeleteLingoLiftTeacherFavoriteMutation();

  const { data: tasksList, isLoading: isLoadingTasks } =
    useGetLingoLiftTeacherDashboardTasksQuery();

  const { data: allClasses, isLoading: isAllClassesLoading } =
    useGetLingoLiftTeacherDashboardClassesQuery();

  const onToggleFavorite = useCallback(
    async (params: ILingoLiftFavoriteParams, isFavorite: boolean) => {
      const previousState = isFavorite;
      try {
        // Optimistically update UI for this specific item
        setOptimisticFavorites(prev => ({
          ...prev,
          [params.favoritableId]: !previousState,
        }));

        if (isFavorite) {
          await deleteLingoLiftTeacherDashboardFavorite(params);
          showSuccessNotification('Removed from favorites');
        } else {
          await postLingoLiftTeacherDashboardToggleFavorite(params);
          showSuccessNotification('Added to favorites');
        }
      } catch (error: unknown) {
        // Revert UI state on error for this specific item
        setOptimisticFavorites(prev => ({
          ...prev,
          [params.favoritableId]: previousState,
        }));

        // Safely access error message with proper type checking
        if (
          error &&
          typeof error === 'object' &&
          'response' in error &&
          error.response &&
          typeof error.response === 'object' &&
          'data' in error.response &&
          error.response.data &&
          typeof error.response.data === 'object' &&
          'error' in error.response.data
        ) {
          showErrorNotification(String(error.response.data.error));
        } else {
          showErrorNotification('An error occurred while updating favorites');
        }
      }
    },
    [
      deleteLingoLiftTeacherDashboardFavorite,
      postLingoLiftTeacherDashboardToggleFavorite,
      showSuccessNotification,
      showErrorNotification,
    ],
  );

  const handleShowFewerClasses = useCallback(() => {
    setSchoolClassesDisplayedCount(LIMIT);
  }, []);

  const handleShowMoreClasses = useCallback(() => {
    setSchoolClassesDisplayedCount(allClasses?.items?.length ?? LIMIT);
  }, [allClasses]);

  const showMoreOrLess = useCallback(() => {
    if ((allClasses?.items?.length ?? 0) <= LIMIT) {
      return null;
    }
    if (schoolClassesDisplayedCount > LIMIT) {
      return (
        <Button sx={styles.listItemLink} variant="text" onClick={handleShowFewerClasses}>
          Show Fewer Classes
        </Button>
      );
    }
    return (
      <Button sx={styles.listItemLink} variant="text" onClick={handleShowMoreClasses}>
        Show More Classes
      </Button>
    );
  }, [allClasses, schoolClassesDisplayedCount, handleShowFewerClasses, handleShowMoreClasses]);

  const handleShowFewerTasks = useCallback(() => {
    setTasksDisplayedCount(LIMIT_TASKS);
  }, []);

  const handleShowMoreTasks = useCallback(() => {
    setTasksDisplayedCount(tasksList?.items?.length ?? LIMIT_TASKS);
  }, [tasksList]);

  const showMoreOrLessTasks = useCallback(() => {
    if ((tasksList?.items?.length ?? 0) <= LIMIT_TASKS) {
      return null;
    }
    if (tasksDisplayedCount > LIMIT_TASKS) {
      return (
        <Button sx={styles.listItemLink} variant="text" onClick={handleShowFewerTasks}>
          Show Fewer Tasks
        </Button>
      );
    }
    return (
      <Button sx={styles.listItemLink} variant="text" onClick={handleShowMoreTasks}>
        Show All Tasks
      </Button>
    );
  }, [tasksList, tasksDisplayedCount, handleShowFewerTasks, handleShowMoreTasks]);

  const renderSection = useCallback(
    (sectionType, index) => {
      const moveSectionUp = () => {
        if (index > 0) {
          const newOrder = [...sectionOrder];
          [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
          setSectionOrder(newOrder);
          setToLocalStorage('teacherDashboardSectionOrder', newOrder);
        }
      };

      const moveSectionDown = () => {
        if (index < sectionOrder.length - 1) {
          const newOrder = [...sectionOrder];
          [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
          setSectionOrder(newOrder);
          setToLocalStorage('teacherDashboardSectionOrder', newOrder);
        }
      };

      switch (sectionType) {
        case DashboardSection.tasks:
          return (
            <Div sx={styles.contentSection}>
              <Div sx={styles.contentSectionPanel}>
                <Typography variant="h2">Tasks ({tasksList?.items.length ?? 0})</Typography>
                <Div sx={styles.contentSectionPanelActions}>
                  {showMoreOrLessTasks()}
                  <LingoLiftDashboardSectionHeader
                    index={index}
                    onMoveUp={moveSectionUp}
                    onMoveDown={moveSectionDown}
                    sectionOrder={sectionOrder}
                  />
                </Div>
              </Div>
              {isLoadingTasks ? (
                <Loader />
              ) : (
                <LingoLiftTeacherDashboardTaskList
                  tasksDisplayedCount={tasksDisplayedCount}
                  tasks={tasksList?.items}
                />
              )}
            </Div>
          );
        case DashboardSection.classes:
          return (
            <Div sx={styles.contentSection}>
              <Div sx={styles.contentSectionPanel}>
                <Typography variant="h2">My Classes ({allClasses?.items?.length})</Typography>
                <Div sx={styles.contentSectionPanelActions}>
                  {showMoreOrLess()}
                  {allClasses?.links?.map((link: ILingoLiftTeacherDashboardClassesLink) => (
                    <Link
                      fontWeight="600"
                      href={link.link}
                      sx={styles.listItemLink}
                      key={link.name}
                    >
                      {link.name}
                    </Link>
                  ))}
                  <LingoLiftDashboardSectionHeader
                    index={index}
                    onMoveUp={moveSectionUp}
                    onMoveDown={moveSectionDown}
                    sectionOrder={sectionOrder}
                  />
                </Div>
              </Div>
              {isAllClassesLoading ? (
                <Loader />
              ) : (
                <>
                  {allClasses?.items?.length ? (
                    allClasses.items
                      .slice(0, schoolClassesDisplayedCount)
                      .map((schoolClass: ILingoLiftTeacherDashboardClass) => (
                        <LingoLiftTeacherDashboardClassesList
                          key={schoolClass.id}
                          {...schoolClass}
                        />
                      ))
                  ) : (
                    <Div sx={styles.emptyClassesMessage}>
                      <Typography variant="h4">You haven't added any classes yet.</Typography>
                      <Typography>
                        Get started and{' '}
                        <Link href={manageClassesUrl} sx={styles.listItemLink}>
                          create your first class now
                        </Link>
                        !
                      </Typography>
                    </Div>
                  )}
                </>
              )}
            </Div>
          );
        case DashboardSection.reports:
          return (
            <Div sx={styles.contentSection}>
              <Div sx={styles.contentSectionPanel}>
                <Typography variant="h2">Reports</Typography>
                <Div sx={styles.contentSectionPanelActions}>
                  {viewReportsLink && tabIndex === 1 && (
                    <Link href={viewReportsLink} sx={styles.listItemLink}>
                      View Reports
                    </Link>
                  )}
                  <LingoLiftDashboardSectionHeader
                    index={index}
                    onMoveUp={moveSectionUp}
                    onMoveDown={moveSectionDown}
                    sectionOrder={sectionOrder}
                  />
                </Div>
              </Div>
              <Div sx={styles.contentReportsSection}>
                <TabsContainer
                  tabs={reportTabs}
                  tabIndex={tabIndex}
                  setTabIndex={setTabIndex}
                  isCentered
                >
                  {reportTabs.map((tab, tabIdx) => (
                    <TabContent key={tab.name} index={tabIdx} value={tabIndex}>
                      <LingoLiftTeacherDashboardReport
                        setViewReportsLink={setViewReportsLink}
                        isListenwise={tab.isListenwise}
                        isLingolift={tab.isLingolift}
                      />
                    </TabContent>
                  ))}
                </TabsContainer>
              </Div>
            </Div>
          );
        default:
          return null;
      }
    },
    [
      sectionOrder,
      tasksList,
      isLoadingTasks,
      tasksDisplayedCount,
      showMoreOrLessTasks,
      allClasses,
      isAllClassesLoading,
      schoolClassesDisplayedCount,
      showMoreOrLess,
      viewReportsLink,
      tabIndex,
      reportTabs,
    ],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFilter]);

  return (
    <MainLayout userRole={UserRole.Teacher} isLoading={false}>
      <Grid container>
        <Grid
          item
          sx={styles.feedContainer}
          display={{ xs: 'none', md: 'flex' }}
          xs={0}
          md={4}
          lg={3}
        >
          <Typography variant="h2">My Lesson Feed</Typography>
          <Typography>Lesson Types</Typography>
          <DropdownMenu
            options={dashboardFilters.map(item => item.label)}
            onChange={value => {
              setSelectFilter(typeof value === 'string' ? value : value.label);
              setCurrentPage(1);
            }}
            initialValue={selectedFilter}
            isLoading={isDashboardFiltersLoading}
          />
          {isFetching ? (
            <Div sx={styles.loader}>
              <CircularProgress />
            </Div>
          ) : (
            <>
              {dashboardFeed?.items.map(feed => (
                <LingoLiftTeacherDashboardFeedCard
                  key={feed.id}
                  {...feed}
                  onToggleFavorite={(object, isFavorite) => {
                    const params: ILingoLiftFavoriteParams = {
                      favoritableId: typeof object.id === 'number' ? object.id : feed.id,
                      favoritableType: typeof object.type === 'string' ? object.type : feed.type,
                    };
                    void onToggleFavorite(params, isFavorite);
                  }}
                  isFavorite={optimisticFavorites[feed.id] ?? feed.favorited}
                />
              ))}
              {dashboardFeed?.items && dashboardFeed?.pagination.totalPages > 0 && (
                <PaginationLine
                  currentPage={currentPage}
                  onCurrentPageChange={setCurrentPage}
                  pagination={dashboardFeed?.pagination}
                  size="small"
                />
              )}
            </>
          )}
        </Grid>
        <Grid item xs={12} md={8} lg={9}>
          <ContentLayout>
            <Div sx={styles.contentContainer}>
              <Typography variant="h1">Welcome back, {teacherName}</Typography>
              {sectionOrder.map((sectionType, index) => (
                <Div key={sectionType} sx={styles.contentSection}>
                  {renderSection(sectionType, index)}
                </Div>
              ))}
            </Div>
          </ContentLayout>
        </Grid>
      </Grid>
    </MainLayout>
  );
};

export default LingoLiftTeacherDashboardBrowsePage;
